import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, TouchableOpacity } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { Box, Text } from '@components/Restyle';
import { Member } from '@components/User/Member';
import { User } from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';
import useChatInput from '@hooks/useChatInput';
import { useGetMessageFromCache } from '@hooks/useGetMessageFromCache';
import useMe from '@hooks/useMe';
import { resolveReadReceipts } from '@utils/chatMessageHelper';

import Modal from './Modal';

export type TaskRolesAssignable = 'Assignee' | 'Viewer';

type Props = { onBack?: () => void };

export const ChatMessageReadByModal: React.FC<Props> = ({ onBack }) => {
  const { t } = useTranslation();

  const { me } = useMe();
  const { selectedMessage } = useChatInput();
  const { client } = useApolloClient();
  const cacheIdentifier = client?.cache.identify(selectedMessage);

  const message = useGetMessageFromCache(cacheIdentifier);

  const users = resolveReadReceipts(message, selectedMessage).filter(
    (item: User) => item.id !== me?.id
  );

  if (Platform.OS === 'web') {
    return (
      <Box
        backgroundColor='white'
        borderRadius='m'
        style={{
          height: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 8,
        }}>
        {/* Header */}
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          paddingHorizontal='m'
          paddingVertical='m'
          style={{ flexShrink: 0 }}>
          {/* Back Button */}
          <TouchableOpacity
            onPress={onBack}
            style={{
              padding: 8,
              marginLeft: -8,
            }}>
            <Text variant='buttonLabel' color='greenSecondary'>
              Back
            </Text>
          </TouchableOpacity>

          {/* Title */}
          <Text
            variant='headerLabel'
            flex={1}
            textAlign='center'
            marginRight='xl'>
            {t('models:chat.edit.readBy')}
          </Text>
        </Box>

        {/* Scrollable Content */}
        <Box
          flex={1}
          style={{
            minHeight: 0,
            overflow: 'hidden',
          }}>
          <ScrollView
            style={{
              flex: 1,
              height: '100%',
            }}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              paddingTop: 16,
              paddingBottom: 16,
              paddingHorizontal: 16,
            }}>
            {users?.map((item) => (
              <Box key={item.id} paddingVertical='xs'>
                <Member member={item} />
              </Box>
            ))}
          </ScrollView>
        </Box>
      </Box>
    );
  }

  return (
    <Modal
      goBack={onBack}
      showHeader
      headerTitle={t('models:chat.edit.readBy')}
      showSaveButton={false}
      cancelLabel={t('shared:back')}>
      <FlatList
        showsVerticalScrollIndicator={false}
        data={users}
        renderItem={({ item }) => (
          <Box paddingVertical='xs'>
            <Member member={item} />
          </Box>
        )}
        ListHeaderComponent={<Box marginTop='m' />}
        ListFooterComponent={<Box marginTop='m' />}
        keyExtractor={(item) => item.id}
      />
    </Modal>
  );
};
